import type { ApiResponse } from '@/types'

export interface ErrorDetails {
  code?: string
  field?: string
  details?: Record<string, unknown>
}

export interface StandardError {
  message: string
  code?: string
  statusCode?: number
  details?: ErrorDetails[]
}

/**
 * Standardized error handling utility for consistent API responses
 */
export class ErrorHandler {
  /**
   * Normalize different API error formats into a standard format
   */
  static normalizeError(error: unknown): StandardError {
    // Handle Axios errors
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response: { status: number; data: unknown } }
      const { status, data } = axiosError.response

      // Handle backend API format with statusCode
      if (data && typeof data === 'object') {
        const dataObj = data as Record<string, unknown>
        if (dataObj.statusCode && dataObj.statusCode !== 200) {
          return {
            message: Array.isArray(dataObj.message) ? (dataObj.message as string[])[0] : (dataObj.message as string) || 'Request failed',
            code: (dataObj.error as string) || 'API_ERROR',
            statusCode: dataObj.statusCode as number,
            details: dataObj.details ? [{ details: dataObj.details as Record<string, unknown> }] : undefined,
          }
        }

        // Handle standard error format
        if (dataObj.error || dataObj.message) {
          return {
            message: (dataObj.message as string) || (dataObj.error as string) || 'Request failed',
            code: (dataObj.code as string) || 'API_ERROR',
            statusCode: status,
            details: dataObj.details ? [{ details: dataObj.details as Record<string, unknown> }] : undefined,
          }
        }
      }

      return {
        message: `Request failed with status ${status}`,
        code: 'HTTP_ERROR',
        statusCode: status,
      }
    }

    // Handle network errors
    if (error && typeof error === 'object' && 'request' in error) {
      return {
        message: 'Network error - please check your connection',
        code: 'NETWORK_ERROR',
        statusCode: 0,
      }
    }

    // Handle JavaScript errors
    if (error && typeof error === 'object' && 'message' in error) {
      return {
        message: (error as Error).message,
        code: 'CLIENT_ERROR',
        statusCode: 0,
      }
    }

    // Fallback
    return {
      message: 'An unexpected error occurred',
      code: 'UNKNOWN_ERROR',
      statusCode: 0,
    }
  }

  /**
   * Create a standardized API response from an error
   */
  static createErrorResponse<T = unknown>(error: unknown): ApiResponse<T> {
    const normalizedError = this.normalizeError(error)

    return {
      success: false,
      error: normalizedError.message,
      code: normalizedError.code,
      statusCode: normalizedError.statusCode,
      details: normalizedError.details,
    }
  }

  /**
   * Create a standardized success response
   */
  static createSuccessResponse<T>(data: T, message?: string): ApiResponse<T> {
    return {
      success: true,
      data,
      message,
    }
  }

  /**
   * Check if an API response indicates success
   */
  static isSuccess<T>(response: ApiResponse<T>): response is ApiResponse<T> & { success: true; data: T } {
    return response.success === true && response.data !== undefined
  }

  /**
   * Extract error message from various error formats
   */
  static getErrorMessage(error: unknown): string {
    const normalized = this.normalizeError(error)
    return normalized.message
  }

  /**
   * Log errors consistently across the application
   */
  static logError(error: unknown, context?: string): void {
    const normalized = this.normalizeError(error)

    const logData = {
      context: context || 'Unknown',
      message: normalized.message,
      code: normalized.code,
      statusCode: normalized.statusCode,
      details: normalized.details,
      timestamp: new Date().toISOString(),
    }

    console.error(`[${context || 'Error'}]:`, logData)

    // In production, you might want to send this to an error tracking service
    if (process.env.NODE_ENV === 'production') {
      // Example: Send to error tracking service
      // errorTrackingService.captureError(normalized, context)
    }
  }

  /**
   * Check if error should trigger logout (auth-related errors)
   */
  static isAuthError(error: unknown): boolean {
    const normalized = this.normalizeError(error)

    return (
      normalized.statusCode === 401 ||
      normalized.code === 'UNAUTHORIZED' ||
      normalized.code === 'TOKEN_EXPIRED' ||
      normalized.code === 'INVALID_TOKEN'
    )
  }

  /**
   * Check if error should trigger retry
   */
  static isRetryableError(error: unknown): boolean {
    const normalized = this.normalizeError(error)

    return (
      normalized.statusCode === 429 || // Rate limited
      normalized.statusCode === 502 || // Bad gateway
      normalized.statusCode === 503 || // Service unavailable
      normalized.statusCode === 504 || // Gateway timeout
      normalized.code === 'NETWORK_ERROR'
    )
  }
}

/**
 * Retry utility for handling retryable errors
 */
export class RetryHandler {
  static async withRetry<T>(
    operation: () => Promise<T>,
    options: {
      maxRetries?: number
      baseDelay?: number
      maxDelay?: number
      shouldRetry?: (_error: unknown) => boolean
    } = {}
  ): Promise<T> {
    const {
      maxRetries = 3,
      baseDelay = 1000,
      maxDelay = 10000,
      shouldRetry = ErrorHandler.isRetryableError,
    } = options

    let lastError: unknown

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await operation()
      } catch (error) {
        lastError = error

        if (attempt === maxRetries || !shouldRetry(error)) {
          throw error
        }

        // Exponential backoff with jitter
        const delay = Math.min(
          baseDelay * Math.pow(2, attempt) + Math.random() * 1000,
          maxDelay
        )

        if (process.env.NODE_ENV === 'production') {
          console.log(`Retry attempt ${attempt + 1}/${maxRetries} after ${delay}ms`)
        }

        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }

    throw lastError
  }
}
